/**
 * Storefront API GraphQL queries
 * Product and variant data queries for prices, availability, and details
 */

import { gql } from '@apollo/client/core'
import { PRODUCT_VARIANT_FRAGMENT } from './fragments'

export const GET_PRODUCT_BY_HANDLE = gql`
  query GetProductByHandle($handle: String) {
    product(handle: $handle) {
      id
      featuredImage {
        src
      }
      images(first: 2) {
        edges {
          node {
            altText
            url
          }
        }
      }
      options(first: 10) {
        name
      }
      tags
      variants(first: 50) {
        nodes {
          ...ProductVariantFragment
        }
      }
    }
  }

  ${PRODUCT_VARIANT_FRAGMENT}
`

export const GET_INVENTORY_BY_HANDLE = gql`
  query GetInventoryByHandle($handle: String!) {
    product(handle: $handle) {
      id
      handle
      totalInventory
    }
  }
`

export const QUERY_SEARCH_PRODUCTS = gql`
  query QuerySearchProducts($first: Int!, $value: String, $key: String = "stylecode", $namepsace: String = "related_products") {
    search(first: $first, query: "", productFilters: [{ productMetafield: { value: $value, key: $key, namespace: $namespace } }], unavailableProducts: SHOW) {
      edges {
        node {
          ... on Product {
            metafields(
              identifiers: [
                { key: "swatch_image", namespace: $namespace }
                { key: "swatch_hex", namespace: $namespace }
                { key: "swatch_label", namespace: $namespace }
              ]
            ) {
              value
              key
            }
            handle
            variants(first: 1) {
              nodes {
                ...ProductVariantFragment
              }
            }
          }
        }
      }
    }
  }

  ${PRODUCT_VARIANT_FRAGMENT}
`

export const QUERY_PRODUCT_PRICE = gql`
query allProducts($country: CountryCode, $query: String, $count: Int) @inContext(country: $country) {
  products(first: $count, query: $query) {
    edges {
      node {
        title
        id
        handle
        variants(first: 1) {
          edges {
            node {
              title
              price {
                amount
                currencyCode #active local currency
              }
            }
          }
        }
      }
    }
  }
}
`

/**
 * Availability Status / Release Date for a batch of handles, as one aliased
 * lookup per handle in a single request.
 *
 * The Athos feed carries no availability-status field (verified against site
 * `at2ysw`, 2026-08-11: a Coming Soon product returns `available: "true"` and
 * real in-stock sizes), so Quick Add has no way to tell a pre-release product
 * from an ordinary in-stock one. Both metafields are `PUBLIC_READ`, so the
 * Storefront API is the interim source until the AMC feed exposes
 * `mfield_custom_availability_status`.
 *
 * Deliberately NOT `products(first:, query: "handle:…")`. **This store's
 * Storefront API ignores the `query` argument outright** — verified 2026-08-11,
 * where `query: "handle:zzzz-not-a-real-product"` still returned the first five
 * products in default catalogue order, as did every quoting and `OR` variant.
 * A handle-filtered search therefore returns arbitrary products rather than
 * failing, which is the worst shape a bug can take here: the lookup silently
 * resolves the wrong products and a Coming Soon item sails through the gate.
 * Aliased single-product lookups are exact, and cost about the same (7 for 3).
 *
 * Note `getProductPrices` below still uses the `products(query:)` form and is
 * broken for the same reason — out of scope for RR-227, flagged separately.
 */
export const buildProductAvailabilityQuery = (count: number) => {
  const params = Array.from({ length: count }, (_, index) => `$h${index}: String!`).join(', ')
  const lookups = Array.from(
    { length: count },
    (_, index) => `
  p${index}: product(handle: $h${index}) {
    handle
    availabilityStatus: metafield(namespace: "custom", key: "availability_status") { value }
    releaseDate: metafield(namespace: "custom", key: "release_date") { value }
  }`
  ).join('')

  return gql(`query productAvailability(${params}) {${lookups}\n}`)
}

export const QUERY_PRODUCT_PRICE_B2B = gql`
query allProductsB2B($country: CountryCode, $query: String, $count: Int) @inContext(country: $country) {
  products(first: $count, query: $query) {
    edges {
      node {
        title
        id
        handle
        variants(first: 1) {
          edges {
            node {
              title
              price {
                amount
                currencyCode #active local currency
              }
              compareAtPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  }
}
`
